import { supabase } from "@/integrations/supabase/client";

export interface ReferralStats {
  id: number;
  referrer_id: number;
  referred_user_id: number;
  subscription_id: number;
  created_at: string;
  reward_cycle: number;
  reward_amount: number;
  reward_granted: boolean;
  referrer_name?: string;
  referred_user_name?: string;
}

export interface ReferralReward {
  id: number;
  referrer_id: number;
  cycle_number: number;
  subscribers_in_cycle: number;
  reward_amount: number;
  reward_status: 'pending' | 'paid' | 'failed';
  paid_at?: string;
  created_at: string;
  referrer_name?: string;
}

export interface ReferralSummary {
  referrer_id: number;
  referrer_name: string;
  referral_code: string;
  total_referrals: number;
  total_earnings: number;
  pending_earnings: number;
  paid_earnings: number;
  current_cycle_progress: number;
  next_reward_amount: number;
}

// Fetch referral stats summary (for dashboard cards)
export const fetchReferralStatsOverview = async (): Promise<{
  totalReferrals: number;
  rewardedReferrals: number;
  totalRewardAmount: number;
  uniqueReferrers: number;
}> => {
  try {
    // Get total referrals count (with active users only)
    const { count: totalReferrals, error: totalError } = await supabase
      .from('referral_stats')
      .select(`
        *,
        referred_user:referred_user_id(is_active)
      `, { count: 'exact', head: true })
      .eq('referred_user.is_active', true);

    if (totalError) throw totalError;

    // Get rewarded referrals and total reward amount (with active users only)
    const { data: rewardedData, error: rewardedError } = await supabase
      .from('referral_stats')
      .select(`
        reward_amount, 
        referrer_id,
        referred_user:referred_user_id(is_active)
      `)
      .eq('reward_granted', true)
      .eq('referred_user.is_active', true);

    if (rewardedError) throw rewardedError;

    const rewardedReferrals = rewardedData?.length || 0;
    const totalRewardAmount = rewardedData?.reduce((sum, item) => sum + Number(item.reward_amount), 0) || 0;
    const uniqueReferrers = new Set(rewardedData?.map(item => item.referrer_id) || []).size;

    return {
      totalReferrals: totalReferrals || 0,
      rewardedReferrals,
      totalRewardAmount,
      uniqueReferrers,
    };
  } catch (error) {
    console.error('Error fetching referral stats overview:', error);
    throw error;
  }
};

// Fetch referral stats with user details (only for active users)
export const fetchReferralStats = async (filters?: {
  referrer_id?: number;
  limit?: number;
  offset?: number;
}): Promise<{ data: ReferralStats[]; total: number }> => {
  let query = supabase
    .from('referral_stats')
    .select(`
      *,
      referrer:referrer_id(name),
      referred_user:referred_user_id(name, is_active)
    `, { count: 'exact' })
    .eq('referred_user.is_active', true)  // Only include stats for active users
    .order('created_at', { ascending: false });

  if (filters?.referrer_id) {
    query = query.eq('referrer_id', filters.referrer_id);
  }

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  if (filters?.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching referral stats:', error);
    throw error;
  }

  const formattedData: ReferralStats[] = (data || []).map(item => ({
    ...item,
    referrer_name: item.referrer?.name,
    referred_user_name: item.referred_user?.name,
  }));

  return {
    data: formattedData,
    total: count || 0,
  };
};

// Fetch referral rewards summary (for dashboard cards)
export const fetchReferralRewardsOverview = async (): Promise<{
  totalRewards: number;
  pendingRewards: number;
  paidRewards: number;
  totalAmount: number;
}> => {
  try {
    // Get all rewards data for calculations
    const { data: allRewards, error } = await supabase
      .from('referral_rewards')
      .select('reward_status, reward_amount');

    if (error) throw error;

    const totalRewards = allRewards?.length || 0;
    const pendingRewards = allRewards?.filter(r => r.reward_status === 'pending').length || 0;
    const paidRewards = allRewards?.filter(r => r.reward_status === 'paid').length || 0;
    const totalAmount = allRewards?.reduce((sum, r) => sum + Number(r.reward_amount), 0) || 0;

    return {
      totalRewards,
      pendingRewards,
      paidRewards,
      totalAmount,
    };
  } catch (error) {
    console.error('Error fetching referral rewards overview:', error);
    throw error;
  }
};

// Fetch referral rewards with user details
export const fetchReferralRewards = async (filters?: {
  referrer_id?: number;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: ReferralReward[]; total: number }> => {
  let query = supabase
    .from('referral_rewards')
    .select(`
      *,
      referrer:referrer_id(name)
    `, { count: 'exact' })
    .order('created_at', { ascending: false });

  if (filters?.referrer_id) {
    query = query.eq('referrer_id', filters.referrer_id);
  }

  if (filters?.status) {
    query = query.eq('reward_status', filters.status);
  }

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  if (filters?.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching referral rewards:', error);
    throw error;
  }

  const formattedData: ReferralReward[] = (data || []).map(item => ({
    ...item,
    referrer_name: item.referrer?.name,
  }));

  return {
    data: formattedData,
    total: count || 0,
  };
};

// Fetch referral summary for all users or specific user
export const fetchReferralSummary = async (userId?: number): Promise<ReferralSummary[]> => {
  try {
    // Build the query based on whether we want a specific user or all users
    let query = supabase
      .from('profiles')
      .select(`
        id,
        name,
        referral_code
      `)
      .not('referral_code', 'is', null);

    if (userId) {
      query = query.eq('id', userId);
    }

    const { data: profiles, error: profilesError } = await query;

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
      throw profilesError;
    }

    if (!profiles || profiles.length === 0) {
      return [];
    }

    // Get referral stats for each profile
    const profileIds = profiles.map(p => p.id);
    
    const { data: referralStats, error: statsError } = await supabase
      .from('referral_stats')
      .select('referrer_id, referred_user_id')
      .in('referrer_id', profileIds);

    if (statsError) {
      console.error('Error fetching referral stats:', statsError);
      throw statsError;
    }

    // Get referral rewards for each profile
    const { data: referralRewards, error: rewardsError } = await supabase
      .from('referral_rewards')
      .select('referrer_id, reward_amount, reward_status')
      .in('referrer_id', profileIds);

    if (rewardsError) {
      console.error('Error fetching referral rewards:', rewardsError);
      throw rewardsError;
    }

    // Calculate summary for each profile
    const summaries: ReferralSummary[] = profiles.map(profile => {
      const stats = referralStats?.filter(rs => rs.referrer_id === profile.id) || [];
      const rewards = referralRewards?.filter(rr => rr.referrer_id === profile.id) || [];
      
      const totalReferrals = new Set(stats.map(s => s.referred_user_id)).size;
      const totalEarnings = rewards.reduce((sum, r) => sum + (r.reward_amount || 0), 0);
      const pendingEarnings = rewards
        .filter(r => r.reward_status === 'pending')
        .reduce((sum, r) => sum + (r.reward_amount || 0), 0);
      const paidEarnings = rewards
        .filter(r => r.reward_status === 'paid')
        .reduce((sum, r) => sum + (r.reward_amount || 0), 0);
      
      const currentCycleProgress = totalReferrals % 5;
      const nextRewardAmount = currentCycleProgress === 0 ? 0 : 
        (200 + (Math.ceil(totalReferrals / 5) - 1) * 20) * (5 - currentCycleProgress);

      return {
        referrer_id: profile.id,
        referrer_name: profile.name || 'Unknown',
        referral_code: profile.referral_code || '',
        total_referrals: totalReferrals,
        total_earnings: totalEarnings,
        pending_earnings: pendingEarnings,
        paid_earnings: paidEarnings,
        current_cycle_progress: currentCycleProgress,
        next_reward_amount: nextRewardAmount,
      };
    });

    return summaries.sort((a, b) => 
      b.total_referrals - a.total_referrals || b.total_earnings - a.total_earnings
    );
  } catch (error) {
    console.error('Error fetching referral summary:', error);
    throw error;
  }
};

// Mark reward as paid
export const markRewardAsPaid = async (rewardId: number): Promise<void> => {
  const { error } = await supabase
    .from('referral_rewards')
    .update({
      reward_status: 'paid',
      paid_at: new Date().toISOString(),
    })
    .eq('id', rewardId);

  if (error) {
    console.error('Error marking reward as paid:', error);
    throw error;
  }
};

// Mark reward as failed
export const markRewardAsFailed = async (rewardId: number): Promise<void> => {
  const { error } = await supabase
    .from('referral_rewards')
    .update({
      reward_status: 'failed',
    })
    .eq('id', rewardId);

  if (error) {
    console.error('Error marking reward as failed:', error);
    throw error;
  }
};

// Get user's referral code
export const getUserReferralCode = async (userId: number): Promise<string | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('referral_code')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching user referral code:', error);
    throw error;
  }

  return data?.referral_code || null;
};

// Find user by referral code
export const findUserByReferralCode = async (referralCode: string): Promise<{ id: number; name: string } | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name')
    .eq('referral_code', referralCode)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('Error finding user by referral code:', error);
    throw error;
  }

  return data;
};

// Interface for referred users
export interface ReferredUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  is_active: boolean;
  has_subscription: boolean; // True only if user has an active subscription made through this referrer
  subscription_status?: string;
}

// Fetch users referred by a specific user (only active users)
export const fetchReferredUsers = async (referrerId: number): Promise<ReferredUser[]> => {
  // First, get the referred users (only active ones)
  const { data: users, error: usersError } = await supabase
    .from('profiles')
    .select(`
      id,
      name,
      email,
      registered_at,
      is_active
    `)
    .eq('referred_by', referrerId)
    .eq('is_active', true)  // Only fetch active users
    .order('registered_at', { ascending: false });

  if (usersError) {
    console.error('Error fetching referred users:', usersError);
    throw usersError;
  }

  if (!users || users.length === 0) {
    return [];
  }

  // Get user IDs for subscription lookup
  const userIds = users.map(user => user.id);

  // Fetch subscription information separately
  // Only count subscriptions that were made through this specific referrer
  const { data: subscriptions, error: subscriptionsError } = await supabase
    .from('user_subscriptions')
    .select('user_id, status, referrer_id')
    .in('user_id', userIds)
    .eq('status', 'active')
    .eq('referrer_id', referrerId); // Only subscriptions made through this referrer

  if (subscriptionsError) {
    console.error('Error fetching user subscriptions:', subscriptionsError);
    // Continue without subscription data rather than failing
  }

  // Create a set of user IDs with active subscriptions made through this referrer
  const referralSubscriptionUserIds = new Set(
    (subscriptions || []).map(sub => sub.user_id)
  );

  // Transform the data to include subscription information
  const formattedData: ReferredUser[] = users.map(user => {
    const hasReferralSubscription = referralSubscriptionUserIds.has(user.id);
    
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      created_at: user.registered_at, // Map registered_at to created_at for interface compatibility
      is_active: user.is_active,
      has_subscription: hasReferralSubscription,
      subscription_status: hasReferralSubscription ? 'active' : 'none',
    };
  });

  return formattedData;
};

// Get complete referral information for a user
export const getUserReferralInfo = async (userId: number) => {
  try {
    // Fetch user's referral code and who referred them
    const { data: userProfile, error: profileError } = await supabase
      .from('profiles')
      .select(`
        referral_code,
        referred_by,
        referrer:referred_by(name, email)
      `)
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      throw profileError;
    }

    // Fetch referral summary using the RPC function
    let summary = null;
    try {
      const summaryData = await fetchReferralSummary(userId);
      summary = summaryData.length > 0 ? summaryData[0] : null;
    } catch (summaryError) {
      console.warn('Could not fetch referral summary:', summaryError);
      // Continue without summary data
    }

    // Fetch referred users
    const referredUsers = await fetchReferredUsers(userId);

    // Fetch referral rewards
    const { data: rewards } = await fetchReferralRewards({ referrer_id: userId });

    // Count total referred users vs paid subscribers
    const totalReferredUsers = referredUsers.length;
    const totalPaidSubscribers = referredUsers.filter(user => user.has_subscription).length;

    return {
      referralCode: userProfile?.referral_code,
      referredBy: userProfile?.referrer,
      totalReferredUsers,
      totalPaidSubscribers,
      totalEarnings: summary?.total_earnings || 0,
      pendingEarnings: summary?.pending_earnings || 0,
      paidEarnings: summary?.paid_earnings || 0,
      currentCycleProgress: summary?.current_cycle_progress || 0,
      nextRewardAmount: summary?.next_reward_amount || 0,
      referredUsers,
      rewards,
    };
  } catch (error) {
    console.error('Error fetching user referral info:', error);
    throw error;
  }
};

// Link a user to a referrer via referral code
export const linkUserToReferrer = async (userId: number, referralCode: string): Promise<void> => {
  // First, find the referrer by their referral code
  const referrer = await findUserByReferralCode(referralCode);
  
  if (!referrer) {
    throw new Error('Invalid referral code. No user found with this code.');
  }

  if (referrer.id === userId) {
    throw new Error('You cannot refer yourself.');
  }

  // Check if user already has a referrer
  const { data: currentProfile, error: profileError } = await supabase
    .from('profiles')
    .select('referred_by')
    .eq('id', userId)
    .single();

  if (profileError) {
    console.error('Error checking current referrer:', profileError);
    throw profileError;
  }

  if (currentProfile?.referred_by) {
    throw new Error('This user already has a referrer assigned.');
  }

  // Update the user's profile to set the referrer
  const { error } = await supabase
    .from('profiles')
    .update({
      referred_by: referrer.id,
    })
    .eq('id', userId);

  if (error) {
    console.error('Error linking user to referrer:', error);
    throw error;
  }
};

// Remove referrer link from a user
export const unlinkUserFromReferrer = async (userId: number): Promise<void> => {
  const { error } = await supabase
    .from('profiles')
    .update({
      referred_by: null,
    })
    .eq('id', userId);

  if (error) {
    console.error('Error unlinking user from referrer:', error);
    throw error;
  }
}; 